import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { eAssetType, EnumeratedTypesRepositoryService, eOrderType, eSearchJurisdictionState, JurisdictionInfo, Order, OrderManagerService, OrderUIConfiguration, SelectOption, UIConfigurationService, ValidationProblem, ValidationService, SearchParameters, ePartyType } from 'reg-hub-common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-multi-search-criteria',
  templateUrl: './multi-search-criteria.component.html',
  styleUrls: ['./multi-search-criteria.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MultiSearchCriteriaComponent extends SteppedComponent {
  private destroyRef = inject(DestroyRef);

  protected orderTypes = [eOrderType.assetSearch];
  protected searchForm!: FormGroup;
  protected loading = false;
  protected assetTypes: SelectOption[] = [];
  protected orderJurisdictions: string[] = [];
  protected configs: OrderUIConfiguration[] = [];

  protected serialNumberError$ = new BehaviorSubject<string | null | undefined>(null);
  protected searchFromDateError$ = new BehaviorSubject<string | null | undefined>(null);
  protected firstNameError$ = new BehaviorSubject<string | null | undefined>(null);
  protected middleNameError$ = new BehaviorSubject<string | null | undefined>(null);
  protected lastNameError$ = new BehaviorSubject<string | null | undefined>(null);
  protected busNameError$ = new BehaviorSubject<string | null | undefined>(null);
  protected dateOfBirthError$ = new BehaviorSubject<string | null | undefined>(null);

  constructor(orderManager: OrderManagerService,
    validationService: ValidationService,
    private formBuilder: FormBuilder,
    private enumeratedTypesRepo: EnumeratedTypesRepositoryService) {
    super(orderManager, validationService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loading = true;

    this.searchForm = this.formBuilder.group({
      exactOnlyResults: this.formBuilder.control(this.order.searchParameters?.exactOnlyResults ?? false),
      serialNumber: this.formBuilder.control(this.order.assets?.at(0)?.serialNumber),
      partyTypeID: this.formBuilder.control(this.order.parties?.at(0)?.partyTypeID ?? ePartyType.individualDebtor),
      firstName: this.formBuilder.control(this.order.parties?.at(0)?.firstName),
      middleName: this.formBuilder.control(this.order.parties?.at(0)?.middleName),
      lastName: this.formBuilder.control(this.order.parties?.at(0)?.lastName),
      busName: this.formBuilder.control(this.order.parties?.at(0)?.busName),
      dateOfBirth: this.formBuilder.control(this.order.parties?.at(0)?.dateOfBirth),
    });

    this.orderJurisdictions = this.order.searchParameters?.searchJurisdictions?.map(j => j.jurisdiction) ?? [];

    //Need to refactor. UI configs should return the jurisdiction they are, then we can get all jurisdiction asset types and
    //give distinct ones
    if (this.order.orderTypeID === eOrderType.assetMultiSearch) {
      this.enumeratedTypesRepo.getAssetTypesAsSelectOptions(eOrderType.assetSearch, 'SK').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(jurisdictionTypes => {
        this.assetTypes = jurisdictionTypes;
      });
    } else if (this.order.orderTypeID === eOrderType.debtorMultiSearch) {
      this.searchForm.get('partyTypeID')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
        if (val === ePartyType.individualDebtor) {
          this.searchForm.get('busName')?.setValue(undefined);
        } else if (val === ePartyType.businessDebtor) {
          this.searchForm.get('firstName')?.setValue(undefined);
          this.searchForm.get('middleName')?.setValue(undefined);
          this.searchForm.get('lastName')?.setValue(undefined);
          this.searchForm.get('dateOfBirth')?.setValue(undefined);
        }
      });
    }
  }

  protected onJurisdictionSelected(jurisdictions: JurisdictionInfo[]) {
    this.orderJurisdictions = jurisdictions.map(j => j.jurisdictionCode);
  }

  override onSaving(): void {
    if (!this.order.searchParameters) {
      this.order.searchParameters = {} as SearchParameters;
    }

    if (this.order.orderTypeID === eOrderType.assetMultiSearch) {
      const asset = this.order.assets?.at(0);

      if (asset) {
        asset.assetTypeID = eAssetType.None;
        asset.serialNumber = this.searchForm.get('serialNumber')?.value;
      }
    } else if (this.order.orderTypeID === eOrderType.debtorMultiSearch) {
      this.order.parties = [this.searchForm.value];
      this.order.searchParameters.exactOnlyResults = this.searchForm.get('exactOnlyResults')?.value;
    }

    this.order.searchParameters.searchJurisdictions = this.orderJurisdictions.map(j => {
      return {
        id: this.order.searchParameters?.searchJurisdictions.find(sj => sj.jurisdiction == j)?.id,
        jurisdiction: j,
        hitDirect: false,
        searchParametersID: this.order.searchParameters?.id,
        searchJurisdictionStateID: eSearchJurisdictionState.None
      };
    });
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    if (order.orderTypeID === eOrderType.assetMultiSearch) {
      return !!order.assets?.at(0)?.serialNumber && (order.searchParameters?.searchJurisdictions.length ?? 0) > 0 ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;
    } else if (order.orderTypeID === eOrderType.debtorMultiSearch) {
      let criteria: string | null | undefined;
      const party = order.parties?.at(0);
      let state = (order.searchParameters?.searchJurisdictions.length ?? 0) > 0 ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;

      if (state === StepValidationState.ValidationError) {
        return state;
      }

      if (party?.partyTypeID === ePartyType.individualDebtor) {
        if (!party.firstName && !party.lastName) {
          state = StepValidationState.ValidationError;
        }
      } else if (party?.partyTypeID === ePartyType.businessDebtor) {
        if (!party.busName) {
          state = StepValidationState.ValidationError;
        }
      }

      return state;
    } else {
      return StepValidationState.DefaultState;
    }
  }
}
